import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { normalize } from '../../utils/normalize';

// categories prop: [{ id, name, subcategories: [{ id, name }] }]
// groupUnits prop: [item, ...] — if provided, save updates all units in the group

function subsForCat(categories, catName) {
  return categories.find(c => c.name === catName)?.subcategories?.map(s => s.name) || [];
}

const IconX = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

function initialForm(item) {
  return {
    name: item?.name || '',
    category: item?.category || '',
    subcategory: item?.subcategory || '',
    quantity: item?.quantity ?? 1,
    notes: item?.notes || '',
    pack_id: item?.pack_id || null,
    unit_label: item?.unit_label || null,
  };
}

function initialPackForm() {
  return { name: '', category: '', subcategory: '' };
}

export default function ItemFormModal({ item, groupUnits, categories = [], onClose }) {
  const isEdit = !!item;

  // ── Item form state ──────────────────────────────────────────────────────────
  const [mode, setMode] = useState('item');
  const [form, setForm] = useState(() => initialForm(item));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nameOptions, setNameOptions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lockedFromExisting, setLockedFromExisting] = useState(false);
  const nameInputRef = useRef(null);

  // ── Pack form state ──────────────────────────────────────────────────────────
  const [packForm, setPackForm] = useState(() => initialPackForm());
  const [packNameOptions, setPackNameOptions] = useState([]);
  const [showPackSuggestions, setShowPackSuggestions] = useState(false);
  const [packLockedFromExisting, setPackLockedFromExisting] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemSuggestions, setShowItemSuggestions] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemDropdownPos, setItemDropdownPos] = useState(null);
  const itemSearchInputRef = useRef(null);

  // ── Set default category once categories load (new item only) ────────────────
  useEffect(() => {
    if (!isEdit && !groupUnits && !form.category && categories.length > 0) {
      const first = categories[0];
      setForm(f => ({
        ...f,
        category: first.name,
        subcategory: first.subcategories?.[0]?.name || '',
      }));
    }
  }, [categories]); // eslint-disable-line

  // ── Set default pack category once categories load (pack mode only) ──────────
  useEffect(() => {
    if (!isEdit && !groupUnits && mode === 'pack' && !packForm.category && categories.length > 0) {
      setPackForm(f => ({ ...f, category: categories[0].name }));
    }
  }, [categories, mode]); // eslint-disable-line

  // ── Fetch item names for autocomplete ────────────────────────────────────────
  useEffect(() => {
    if (isEdit || groupUnits) return;
    api.get('/inventory/items/names').then(res => setNameOptions(res.data)).catch(() => {});
  }, [isEdit, groupUnits]);

  // ── Fetch pack data when in pack mode ────────────────────────────────────────
  useEffect(() => {
    if (isEdit || groupUnits || mode !== 'pack') return;
    Promise.all([
      api.get('/inventory/items?available=true'),
      api.get('/inventory/packs/names'),
    ])
      .then(([itemsRes, packsRes]) => {
        setAvailableItems(itemsRes.data);
        setPackNameOptions(packsRes.data);
      })
      .catch(() => {});
  }, [isEdit, groupUnits, mode]);

  // ── Item search dropdown position ────────────────────────────────────────────
  // El dropdown es position:fixed → hay que recalcular sus coordenadas en cada
  // scroll (capture: true para atrapar el scroll de contenedores internos) y
  // resize, o queda anclado a la posición vieja del viewport al desplazarse.
  useEffect(() => {
    if (!showItemSuggestions || !itemSearchInputRef.current) return;
    const update = () => {
      const rect = itemSearchInputRef.current.getBoundingClientRect();
      setItemDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showItemSuggestions]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const categoryNames = categories.map(c => c.name);
  const subcategories = subsForCat(categories, form.category);
  const packSubcategories = subsForCat(categories, packForm.category);

  const nameExists = !isEdit && !groupUnits && !!nameOptions.find(o => o.name === form.name.trim());
  const matchesWords = (name, query) =>
    normalize(query).split(/\s+/).filter(Boolean).every(word => normalize(name).includes(word));

  const sortByLabel = (a, b) => {
    if (a.name !== b.name) return a.name.localeCompare(b.name);
    const numA = parseInt(a.unit_label || '') || 0;
    const numB = parseInt(b.unit_label || '') || 0;
    return numA - numB;
  };

  const filteredSuggestions = nameOptions
    .filter(o => matchesWords(o.name, form.name) && normalize(o.name) !== normalize(form.name))
    .sort(sortByLabel)
    .slice(0, 20);

  const packNameMatch = packNameOptions.find(o => o.name === packForm.name.trim());
  const filteredPackSuggestions = packNameOptions
    .filter(o => matchesWords(o.name, packForm.name) && normalize(o.name) !== normalize(packForm.name))
    .sort(sortByLabel)
    .slice(0, 20);

  const filteredAvailableItems = availableItems
    .filter(i => !selectedItems.find(s => s.id === i.id))
    .filter(i => itemSearch.length > 0 && matchesWords(i.name, itemSearch))
    .sort(sortByLabel)
    .slice(0, 20);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSelectSuggestion = (option) => {
    setForm(f => ({ ...f, name: option.name, category: option.category, subcategory: option.subcategory }));
    setLockedFromExisting(true);
    setShowSuggestions(false);
  };

  const handleCategoryChange = (cat) => {
    const subs = subsForCat(categories, cat);
    setForm(f => ({ ...f, category: cat, subcategory: subs[0] || '' }));
  };

  const handlePackCategoryChange = (cat) => {
    const subs = subsForCat(categories, cat);
    setPackForm(f => ({ ...f, category: cat, subcategory: subs[0] || '' }));
  };

  const handleSelectPackSuggestion = (option) => {
    setPackForm(f => ({
      ...f,
      name: option.name,
      category: option.category || categoryNames[0] || '',
      subcategory: option.subcategory || '',
    }));
    setPackLockedFromExisting(true);
    setShowPackSuggestions(false);
  };

  const handleSelectAvailableItem = (itm) => {
    setSelectedItems(prev => [...prev, itm]);
    setItemSearch('');
    setShowItemSuggestions(false);
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (groupUnits) {
        await Promise.all(groupUnits.map(u =>
          api.put(`/inventory/items/${u.id}`, {
            name: form.name.trim(),
            category: form.category,
            subcategory: form.subcategory,
            quantity: u.quantity,
            notes: u.notes || null,
            pack_id: u.pack_id || null,
            unit_label: u.unit_label || null,
          })
        ));
      } else if (isEdit) {
        await api.put(`/inventory/items/${item.id}`, form);
      } else {
        await api.post('/inventory/items', form);
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitPack = async (e) => {
    e.preventDefault();
    if (!packForm.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      let packId;
      if (packNameMatch) {
        await api.put(`/inventory/packs/${packNameMatch.id}`, {
          name: packForm.name.trim(),
          category: packForm.category || packNameMatch.category,
          subcategory: packForm.subcategory || packNameMatch.subcategory || null,
          quantity: packNameMatch.quantity ?? 0,
          notes: packNameMatch.notes || null,
        });
        packId = packNameMatch.id;
      } else {
        const { data: newPack } = await api.post('/inventory/packs', {
          name: packForm.name.trim(),
          category: packForm.category,
          subcategory: packForm.subcategory || null,
        });
        packId = newPack.id;
      }
      await Promise.all(
        selectedItems.map(i =>
          api.put(`/inventory/items/${i.id}`, {
            name: i.name, category: i.category, subcategory: i.subcategory,
            quantity: i.quantity, notes: i.notes || null,
            unit_label: i.unit_label || null, pack_id: packId,
          })
        )
      );
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ── Title ────────────────────────────────────────────────────────────────────
  const title = groupUnits
    ? `Editar grupo: ${groupUnits.length} unidad${groupUnits.length !== 1 ? 'es' : ''}`
    : isEdit
      ? 'Editar ítem'
      : mode === 'pack'
        ? (packNameMatch ? 'Editar pack' : 'Nuevo pack')
        : 'Nuevo ítem';

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" style={{ overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{title}</div>

        {/* Mode toggle (only on create, not group edit) */}
        {!isEdit && !groupUnits && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: 'var(--surface2)', borderRadius: 8, padding: 4 }}>
            <button
              type="button"
              className={`btn btn-sm${mode === 'item' ? ' btn-primary' : ' btn-ghost'}`}
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={() => { setMode('item'); setError(''); }}
            >
              Ítem
            </button>
            <button
              type="button"
              className={`btn btn-sm${mode === 'pack' ? ' btn-primary' : ' btn-ghost'}`}
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={() => { setMode('pack'); setError(''); }}
            >
              Pack
            </button>
          </div>
        )}

        {/* ── ITEM FORM ── */}
        {(isEdit || groupUnits || mode === 'item') && (
          <form onSubmit={handleSubmitItem}>
            {error && <div className="inv-error">{error}</div>}

            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Nombre</label>
              <input
                ref={nameInputRef}
                className="input"
                value={form.name}
                onChange={e => {
                  setForm(f => ({ ...f, name: e.target.value }));
                  setLockedFromExisting(false);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                required
                autoFocus
                autoComplete="off"
              />
              {!isEdit && !groupUnits && showSuggestions && form.name.length > 0 && filteredSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, width: '100%', zIndex: 1000,
                  maxHeight: 200, overflowY: 'auto',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', marginTop: 2,
                }}>
                  {filteredSuggestions.map(option => (
                    <div
                      key={option.name}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                      onMouseDown={() => handleSelectSuggestion(option)}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <div>{option.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                        {option.category} · {option.subcategory}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {nameExists && (
                <p style={{ fontSize: 12, color: 'var(--yellow, #f59e0b)', marginTop: 6 }}>
                  Este ítem ya existe — se agregará una unidad nueva
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Categoría</label>
              <select
                className="input"
                value={form.category}
                onChange={e => handleCategoryChange(e.target.value)}
                disabled={lockedFromExisting}
              >
                {categoryNames.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Subcategoría</label>
              <select
                className="input"
                value={form.subcategory}
                onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))}
                disabled={lockedFromExisting}
              >
                {subcategories.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>

            {lockedFromExisting && (
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: -8, marginBottom: 12 }}>
                Categoría y subcategoría bloqueadas porque el ítem ya existe.
              </p>
            )}

            {!groupUnits && (
              <>
                <div className="form-group">
                  <label className="form-label">Cantidad</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    value={form.quantity}
                    onChange={e => setForm(f => ({ ...f, quantity: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea
                    className="input"
                    rows={3}
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </>
            )}

            {groupUnits && (
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>
                Se actualizarán nombre, categoría y subcategoría de todas las unidades del grupo.
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => onClose(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : groupUnits ? 'Actualizar grupo' : isEdit ? 'Guardar cambios' : 'Crear ítem'}
              </button>
            </div>
          </form>
        )}

        {/* ── PACK FORM ── */}
        {!isEdit && !groupUnits && mode === 'pack' && (
          <form onSubmit={handleSubmitPack}>
            {error && <div className="inv-error">{error}</div>}

            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Nombre del pack</label>
              <input
                className="input"
                value={packForm.name}
                onChange={e => {
                  setPackForm(f => ({ ...f, name: e.target.value }));
                  setPackLockedFromExisting(false);
                  setShowPackSuggestions(true);
                }}
                onFocus={() => setShowPackSuggestions(true)}
                onBlur={() => setTimeout(() => setShowPackSuggestions(false), 150)}
                required
                autoFocus
                autoComplete="off"
              />
              {showPackSuggestions && packForm.name.length > 0 && filteredPackSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, width: '100%', zIndex: 1000,
                  maxHeight: 200, overflowY: 'auto',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', marginTop: 2,
                }}>
                  {filteredPackSuggestions.map(option => (
                    <div
                      key={option.name}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                      onMouseDown={() => handleSelectPackSuggestion(option)}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <div>{option.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                        {option.category}{option.subcategory ? ` · ${option.subcategory}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {packNameMatch && (
                <p style={{ fontSize: 12, color: 'var(--yellow, #f59e0b)', marginTop: 6 }}>
                  Este pack ya existe — se editará el existente
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Categoría</label>
              <select
                className="input"
                value={packForm.category}
                onChange={e => handlePackCategoryChange(e.target.value)}
                disabled={packLockedFromExisting}
              >
                {categoryNames.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Subcategoría (opcional)</label>
              <select
                className="input"
                value={packForm.subcategory}
                onChange={e => setPackForm(f => ({ ...f, subcategory: e.target.value }))}
                disabled={packLockedFromExisting}
              >
                <option value="">— ninguna —</option>
                {packSubcategories.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>

            {packLockedFromExisting && (
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: -8, marginBottom: 12 }}>
                Categoría y subcategoría bloqueadas porque el pack ya existe.
              </p>
            )}

            <div className="form-group">
              <label className="form-label">Ítems del pack</label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={itemSearchInputRef}
                  className="input"
                  placeholder="Buscar ítem disponible..."
                  value={itemSearch}
                  onChange={e => { setItemSearch(e.target.value); setShowItemSuggestions(true); }}
                  onFocus={() => setShowItemSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowItemSuggestions(false), 150)}
                  autoComplete="off"
                />
                {showItemSuggestions && filteredAvailableItems.length > 0 && itemDropdownPos && (
                  <div style={{
                    position: 'fixed',
                    top: itemDropdownPos.top,
                    left: itemDropdownPos.left,
                    width: itemDropdownPos.width,
                    zIndex: 9999,
                    maxHeight: 200, overflowY: 'auto',
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}>
                    {filteredAvailableItems.map(i => (
                      <div
                        key={i.id}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                        onMouseDown={() => handleSelectAvailableItem(i)}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <div>{i.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                          {i.unit_label && <span style={{ marginRight: 6 }}>{i.unit_label}</span>}
                          {i.category} · {i.subcategory}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedItems.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {selectedItems.map(i => (
                    <div key={i.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 10px', background: 'var(--surface2)', borderRadius: 6, fontSize: 13,
                    }}>
                      <div>
                        <span style={{ fontWeight: 500 }}>{i.name}</span>
                        {i.unit_label && <span className="text-dim" style={{ marginLeft: 6, fontSize: 11 }}>{i.unit_label}</span>}
                      </div>
                      <button
                        type="button"
                        className="btn btn-icon btn-ghost"
                        style={{ width: 22, height: 22, color: 'var(--text-dim)' }}
                        onClick={() => setSelectedItems(prev => prev.filter(s => s.id !== i.id))}
                      >
                        <IconX />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {selectedItems.length === 0 && (
                <p className="text-dim" style={{ fontSize: 12, marginTop: 6 }}>
                  Sin ítems seleccionados — podés agregarlos después desde Gestión.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => onClose(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : packNameMatch ? 'Guardar cambios' : 'Crear pack'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
